import { useAuth } from 'react-oidc-context';
import { useEffect, useState } from 'react';
import LoadingScreen from '../components/common/LoadingScreen';
import LandingPage from '../pages/LandingPage';
import AuthTokenSetter from './AuthTokenSetter';
import DebugTokenSetter from './DebugTokenSetter';
import { reportApiError } from '../stores/apiHealthStore';
import { useApiHealthStore } from '../stores/apiHealthStore';
import { clearSigninRequest, hasSigninRequest } from '../utils/facilityWindow';

const isDebugAuth = import.meta.env.VITE_DEBUG_AUTH === 'true';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * When VITE_DEBUG_AUTH=true, bypasses Keycloak entirely and fetches a dev JWT
 * from the API's /auth/dev-token endpoint. The API must be running with
 * DebugSettings:Enabled=true for this to work.
 */
function DebugAuthGuard({ children }: AuthGuardProps) {
  const [ready, setReady] = useState(false);
  const isApiDown = useApiHealthStore((s) => s.isApiDown);

  useEffect(() => {
    if (ready) return;

    fetch('/auth/dev-token')
      .then((res) => {
        if (!res.ok) throw new Error(`Server returned ${res.status} - API may not be running`);
        return res.json();
      })
      .then(() => setReady(true))
      .catch((err) => {
        // Trigger the API-down overlay instead of showing a static error
        reportApiError(err.message);
      });
  }, [ready]);

  // When the API recovers (overlay dismisses), retry the token fetch
  useEffect(() => {
    if (!isApiDown && !ready) {
      fetch('/auth/dev-token')
        .then((res) => {
          if (!res.ok) throw new Error(`Server returned ${res.status}`);
          return res.json();
        })
        .then(() => setReady(true))
        .catch(() => {
          // Still down - the interceptor/overlay will handle it
        });
    }
  }, [isApiDown, ready]);

  if (!ready) {
    return <LoadingScreen />;
  }

  // Children are wrapped, not siblings: DebugTokenSetter installs the auth
  // token in an effect, and rendering the app beside it let queries fire
  // before that happened (401 on the first [Authorize] endpoint).
  return (
    <>
      <DebugTokenSetter>{children}</DebugTokenSetter>
    </>
  );
}

function isTokenExpiredError(err?: { message?: string } | null): boolean {
  const msg = err?.message ?? '';
  return (
    msg.includes('Token is not active') ||
    msg.includes('token') ||
    msg.includes('expired')
  );
}

// oidc-client-ts throws this when the ?code=&state= Keycloak sent back has no
// matching entry in this tab's sessionStorage: a bookmarked Keycloak login
// page, Back or refresh on the callback URL after it was already used, or a
// login finished in a different tab than the one that started it. The Keycloak
// session itself is fine; only this tab's half of the handshake is missing, so
// a fresh sign-in goes straight through without asking for the password.
export function isStaleCallbackError(err?: { message?: string } | null): boolean {
  return (err?.message ?? '').includes('No matching state found in storage');
}

// Kept in sessionStorage so it survives the round trip to Keycloak: one
// automatic retry per tab, then the landing page. If storage is unusable the
// flag can't be set and we don't auto-retry at all (no redirect loop).
const STALE_RETRY_KEY = 'dietech.auth.staleCallbackRetry';

function claimStaleRetry(): boolean {
  try {
    if (sessionStorage.getItem(STALE_RETRY_KEY)) return false;
    sessionStorage.setItem(STALE_RETRY_KEY, '1');
    return sessionStorage.getItem(STALE_RETRY_KEY) === '1';
  } catch {
    return false;
  }
}

function clearStaleRetry() {
  try {
    sessionStorage.removeItem(STALE_RETRY_KEY);
  } catch {
    /* ignore */
  }
}

// Module-level (NOT component state) so it survives the remounts that a
// signinRedirect / re-render triggers. The previous component-local flag reset
// on every remount, which let the silent-refresh → redirect sequence fire over
// and over and produced the "Token is not active" flash loop on the dashboard.
let recoveryAttempted = false;

// Module-level for the same reason: StrictMode runs effects twice, and the
// second run must not start a second sign-in or fall back to the landing page
// while the first is already on its way to Keycloak.
let autoSigninStarted = false;

function KeycloakAuthGuard({ children }: AuthGuardProps) {
  const auth = useAuth();
  const [retrying, setRetrying] = useState(false);
  // 'checking' until the effect below decides, so the landing page doesn't
  // flash up before the automatic retry leaves for Keycloak.
  const [stale, setStale] = useState<'checking' | 'redirecting' | 'settled'>('checking');
  // A window opened by "open in a new window" (utils/facilityWindow.ts) carries
  // ?signin=1. It has empty sessionStorage, so no user, but the person is
  // signed in to Keycloak in the window that opened it: start the sign-in for
  // them instead of showing the Sign In page. 'pending' until the effect below
  // decides, so the landing page doesn't flash up first.
  const [autoSignin, setAutoSignin] = useState<'pending' | 'redirecting' | 'off'>(() =>
    hasSigninRequest(window.location.search) ? 'pending' : 'off',
  );

  // A stale login callback: drop the dead ?code=&state= from the address bar
  // (so a refresh doesn't repeat the error), then start one fresh sign-in.
  useEffect(() => {
    if (!isStaleCallbackError(auth.error)) return;
    window.history.replaceState({}, document.title, window.location.pathname);
    if (!claimStaleRetry()) {
      setStale('settled');
      return;
    }
    setStale('redirecting');
    auth.signinRedirect().catch(() => setStale('settled'));
  }, [auth.error]);

  useEffect(() => {
    if (autoSignin !== 'pending' || auth.isLoading || autoSigninStarted) return;
    autoSigninStarted = true;
    // Off the address bar first: Back, a refresh or a failed sign-in comes
    // back to the plain landing page rather than signing in again.
    clearSigninRequest();
    if (auth.isAuthenticated || auth.error) {
      setAutoSignin('off');
      return;
    }
    setAutoSignin('redirecting');
    auth.signinRedirect().catch(() => setAutoSignin('off'));
  }, [autoSignin, auth.isLoading, auth.isAuthenticated, auth.error]);

  // Back from the Keycloak page can restore this page from the browser's
  // back/forward cache exactly as it was left: on the loading screen. Show the
  // landing page instead.
  useEffect(() => {
    if (autoSignin !== 'redirecting') return;
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setAutoSignin('off');
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [autoSignin]);

  // On an expired token, attempt EXACTLY ONE silent refresh. If it fails, the
  // refresh token itself is dead — clear the stale user (which also stops
  // automaticSilentRenew from storming with repeated 400s) and fall through to a
  // clean login. We must not signinRedirect() in a loop here.
  useEffect(() => {
    if (!auth.error || retrying || recoveryAttempted) return;
    if (!isTokenExpiredError(auth.error)) return;

    recoveryAttempted = true;
    setRetrying(true);
    auth
      .signinSilent()
      .then((user) => {
        // Recovered — allow a future expiration to retry.
        if (user) recoveryAttempted = false;
      })
      .catch(async () => {
        // Refresh token is dead: remove the stale user so automaticSilentRenew
        // stops and the guard falls through to the LandingPage (fresh login).
        try {
          await auth.removeUser();
        } catch {
          /* ignore — we still render the login screen below */
        }
      })
      .finally(() => setRetrying(false));
  }, [auth.error, retrying]);

  // Clear the guard once genuinely authenticated so a later expiry can recover.
  useEffect(() => {
    if (auth.isAuthenticated) {
      recoveryAttempted = false;
      clearStaleRetry();
    }
  }, [auth.isAuthenticated]);

  if (auth.isLoading || retrying) {
    return <LoadingScreen />;
  }

  if (auth.error) {
    // Not something the user can fix from an error screen. After the one
    // automatic retry (or with storage unusable), offer a normal sign-in.
    if (isStaleCallbackError(auth.error)) {
      return stale === 'settled' ? <LandingPage /> : <LoadingScreen />;
    }
    // An expired token is not a hard error — send the user to a clean login
    // instead of an error screen whose "Try Again" re-enters the redirect loop.
    if (isTokenExpiredError(auth.error)) {
      return <LandingPage />;
    }
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <h2>Authentication Error</h2>
        <p>{auth.error.message}</p>
        <button onClick={() => auth.signinRedirect()}>Try Again</button>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return autoSignin === 'off' ? <LandingPage /> : <LoadingScreen />;
  }

  return (
    <>
      <AuthTokenSetter />
      {children}
    </>
  );
}

export default function AuthGuard({ children }: AuthGuardProps) {
  // Bedside, self-order and registered Selective Touch tablets use device
  // tokens / their own auth, not Keycloak
  const path = window.location.pathname;
  if (path.startsWith('/tablet') || path.startsWith('/bedside') || path.startsWith('/self-order') || path.startsWith('/fhir') || path.startsWith('/smart-callback') || path.startsWith('/smart-launch') || path === '/launch' || path === '/terms') {
    return <>{children}</>;
  }

  if (isDebugAuth) {
    return <DebugAuthGuard>{children}</DebugAuthGuard>;
  }
  return <KeycloakAuthGuard>{children}</KeycloakAuthGuard>;
}
